// The 4h chart's structure: support / resistance from the candles' turning
// points, and the desk's "4h bias" with each of its three votes, so the
// trader can check the desk's verdict against the candles by eye.
import type { Candle } from "./indicators";
import { atr } from "./replay/replay";

/** A candle where price turned: its open time and the high (or low) it turned at. */
export interface Touch { time: number; price: number }

export interface SrLevel {
  /** The mean of its turning points. */
  price: number;
  kind: "support" | "resistance";
  /** The turning points behind the level, oldest first. */
  touches: Touch[];
}

/**
 * The engine's swing rule for its support / resistance map (backend
 * marketStructure findRawSwings): a high above the 5 candles before it and
 * the 2 after (a low below them).
 */
const LEFT = 5;
const RIGHT = 2;
/**
 * Turning points within half a 4h candle's average range (ATR 14) are one
 * level. The engine's 1h map joins within 0.5%, which on 4h candles splits one
 * zone into lines a few ticks apart.
 */
const CLUSTER_ATR = 0.5;

export function pivots(candles: Candle[], left = LEFT, right = RIGHT): { highs: Touch[]; lows: Touch[] } {
  const highs: Touch[] = [];
  const lows: Touch[] = [];
  for (let i = left; i < candles.length - right; i++) {
    const c = candles[i]!;
    let isHigh = true;
    let isLow = true;
    for (let j = 1; j <= left; j++) {
      const o = candles[i - j]!;
      if (o.high >= c.high) isHigh = false;
      if (o.low <= c.low) isLow = false;
    }
    for (let j = 1; j <= right; j++) {
      const o = candles[i + j]!;
      if (o.high >= c.high) isHigh = false;
      if (o.low <= c.low) isLow = false;
    }
    if (isHigh) highs.push({ time: c.time, price: c.high });
    if (isLow) lows.push({ time: c.time, price: c.low });
  }
  return { highs, lows };
}

/** Group touches by price, the engine's way: sorted, joined while within `tolerance` of the group's mean. */
function cluster(touches: Touch[], tolerance: number): Touch[][] {
  const sorted = [...touches].sort((a, b) => a.price - b.price);
  const groups: Touch[][] = [];
  for (const t of sorted) {
    const last = groups.at(-1);
    const mid = last ? last.reduce((s, x) => s + x.price, 0) / last.length : 0;
    if (last && Math.abs(t.price - mid) <= tolerance) last.push(t);
    else groups.push([t]);
  }
  return groups;
}

/**
 * Support and resistance from closed candles: the turning points grouped by
 * price (within half an ATR), nearest `perSide` above the price (resistance) and below it
 * (support). Highs and lows share one list, so an old high that later held
 * as a low is a single level with both touches (the engine calls it
 * "flipped").
 */
export function srLevels(closed: Candle[], price: number, perSide = 2): SrLevel[] {
  const { highs, lows } = pivots(closed);
  const range = atr(closed) ?? 0;
  const levels = cluster([...highs, ...lows], CLUSTER_ATR * range).map(group => {
    const mean = group.reduce((s, t) => s + t.price, 0) / group.length;
    return {
      price: mean,
      kind: mean > price ? "resistance" as const : "support" as const,
      touches: group.sort((a, b) => a.time - b.time),
    };
  });
  const above = levels.filter(l => l.kind === "resistance").sort((a, b) => a.price - b.price).slice(0, perSide);
  const below = levels.filter(l => l.kind === "support").sort((a, b) => b.price - a.price).slice(0, perSide);
  return [...above, ...below];
}

// ─── The desk's 4h bias (backend htfBiasFilter computeHTFBiasFromCandles) ────

/** The engine reads the last 259 closed 4h candles (260 fetched, the forming one dropped). */
export const BIAS_CANDLES = 259;

/** A part's vote: towards up, towards down, or neither. */
export type Vote = "up" | "down" | null;

export interface BiasCheck {
  direction: "bullish" | "bearish" | "neutral";
  /** |bullish − bearish| score, 0–1: the checklist's "confidence". */
  confidence: number;
  /** EMA 20 > 50 > 200 votes up (0.4), 20 < 50 < 200 down. e200 is null under 200 candles. */
  ema: { e20: number; e50: number; e200: number | null; vote: Vote };
  /** Over 55 votes up (0.3), under 45 down; 45–55 gives 0.1 to each side. */
  rsi: { value: number; vote: Vote };
  /**
   * The last 3 swing highs and lows (a candle beyond both neighbours): highs
   * all rising votes up (0.3), lows all falling votes down, both or neither
   * is "mixed".
   */
  swings: { highs: Touch[]; lows: Touch[]; vote: Vote };
  /** Closed candles it read. */
  candles: number;
}

/** backend htfBiasFilter emaLast: SMA seed over the first `period`, then EMA; too few prices → the last one. */
function emaLast(prices: number[], period: number): number {
  if (prices.length < period) return prices[prices.length - 1]!;
  const k = 2 / (period + 1);
  let ema = prices.slice(0, period).reduce((a, b) => a + b, 0) / period;
  for (let i = period; i < prices.length; i++) ema = prices[i]! * k + ema * (1 - k);
  return ema;
}

/** backend htfBiasFilter rsiWilder (no losses → 100, too few prices → 50). */
function rsiWilder(prices: number[], period = 14): number {
  if (prices.length < period + 1) return 50;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const change = prices[i]! - prices[i - 1]!;
    if (change > 0) avgGain += change;
    else avgLoss -= change;
  }
  avgGain /= period;
  avgLoss /= period;
  for (let i = period + 1; i < prices.length; i++) {
    const change = prices[i]! - prices[i - 1]!;
    avgGain = (avgGain * (period - 1) + Math.max(0, change)) / period;
    avgLoss = (avgLoss * (period - 1) + Math.max(0, -change)) / period;
  }
  if (avgLoss === 0) return 100;
  return 100 - 100 / (1 + avgGain / avgLoss);
}

/** backend htfBiasFilter getSwingStructure's swing points: beyond both neighbours, strictly. */
function swingPoints(candles: Candle[]): { highs: Touch[]; lows: Touch[] } {
  const highs: Touch[] = [];
  const lows: Touch[] = [];
  for (let i = 1; i < candles.length - 1; i++) {
    const prev = candles[i - 1]!, curr = candles[i]!, next = candles[i + 1]!;
    if (curr.high > prev.high && curr.high > next.high) highs.push({ time: curr.time, price: curr.high });
    if (curr.low < prev.low && curr.low < next.low) lows.push({ time: curr.time, price: curr.low });
  }
  return { highs, lows };
}

/**
 * The desk's 4h bias from closed 4h candles, with each vote — the same
 * numbers, in the same order, as the engine (so the floating-point edge at
 * ±0.2 lands the same way). Null under 20 candles (the engine then says
 * neutral without reading anything).
 */
export function biasCheck(closed: Candle[]): BiasCheck | null {
  if (closed.length < 20) return null;
  const window = closed.slice(-BIAS_CANDLES);
  const closes = window.map(c => c.close);
  const e20 = emaLast(closes, 20);
  const e50 = emaLast(closes, 50);
  const e200 = closes.length >= 200 ? emaLast(closes, 200) : Number.NaN;
  const rsi = rsiWilder(closes, 14);
  const all = swingPoints(window);
  const highs = all.highs.slice(-3);
  const lows = all.lows.slice(-3);
  const rising = highs.length >= 2 && highs.every((h, i) => i === 0 || h.price > highs[i - 1]!.price);
  const falling = lows.length >= 2 && lows.every((l, i) => i === 0 || l.price < lows[i - 1]!.price);

  let bull = 0;
  let bear = 0;
  const emaVote: Vote = e20 > e50 && e50 > e200 ? "up" : e20 < e50 && e50 < e200 ? "down" : null;
  if (emaVote === "up") bull += 0.4;
  else if (emaVote === "down") bear += 0.4;
  const rsiVote: Vote = rsi > 55 ? "up" : rsi < 45 ? "down" : null;
  if (rsiVote === "up") bull += 0.3;
  else if (rsiVote === "down") bear += 0.3;
  else {
    bull += 0.1;
    bear += 0.1;
  }
  const swingVote: Vote = rising && !falling ? "up" : falling && !rising ? "down" : null;
  if (swingVote === "up") bull += 0.3;
  else if (swingVote === "down") bear += 0.3;

  const net = bull - bear;
  return {
    direction: net > 0.2 ? "bullish" : net < -0.2 ? "bearish" : "neutral",
    confidence: Math.abs(net),
    ema: { e20, e50, e200: Number.isNaN(e200) ? null : e200, vote: emaVote },
    rsi: { value: rsi, vote: rsiVote },
    swings: { highs, lows, vote: swingVote },
    candles: window.length,
  };
}

/**
 * The EMA 200 the bias read at each candle close: the engine's EMA over the
 * 259 closed candles ending there. With only 259 candles the 200-candle seed
 * still weighs about half, so it is closer to a 200-candle average than a
 * charting site's EMA 200. Null until 200 candles.
 */
export function biasEma200Series(closes: number[]): (number | null)[] {
  return closes.map((_, i) => {
    const window = closes.slice(Math.max(0, i + 1 - BIAS_CANDLES), i + 1);
    return window.length >= 200 ? emaLast(window, 200) : null;
  });
}
